import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { eErroDaApi, mensagemPara } from '@/lib/api/erros';
import type { Esquema } from '@/lib/api/tipos';

export type Me = Esquema<'Me'>;

/** Só caminhos internos (evita redirecionamento aberto): `/fichas?busca=x` sim, `//site` não. */
export function voltaSegura(volta: string | undefined): string | undefined {
  if (!volta?.startsWith('/')) return undefined;
  if (volta.startsWith('//') || volta.startsWith('/\\')) return undefined;
  if (volta === '/entrar' || volta.startsWith('/entrar?')) return undefined;
  return volta;
}

/** Aluno em todas as empresas: o painel não é para ele (CA-05). */
export function ehSoAluno(me: Me): boolean {
  return !me.superAdmin && me.empresas.length > 0 && me.empresas.every((e) => e.papel === 'aluno');
}

/**
 * O que o layout mostra para esta sessão (spec 003):
 * - `so-aluno`: o painel não é para ele (spec 002, CA-05);
 * - `escolher-empresa`: sem empresa ativa, mas com vínculos (CA-06);
 * - `sem-empresa`: sem vínculo nenhum e sem ser super-admin;
 * - `bloqueado`: o status do vínculo bloqueia o acesso (CA-07);
 * - `painel`: tudo certo.
 */
export type EstadoDoPainel =
  'so-aluno' | 'escolher-empresa' | 'sem-empresa' | 'bloqueado' | 'painel';

export function estadoDoPainel(me: Me): EstadoDoPainel {
  if (ehSoAluno(me)) return 'so-aluno';
  if (me.empresa === null) {
    if (empresasDaEquipe(me).length > 0) return 'escolher-empresa';
    if (!me.superAdmin) return 'sem-empresa';
  }
  // Aluno na empresa ativa e da equipe em outra: escolhe uma em que trabalha.
  if (me.papel === 'aluno' && !me.superAdmin) return 'escolher-empresa';
  if (me.pendencias.includes('ACESSO_BLOQUEADO')) return 'bloqueado';
  return 'painel';
}

/** Empresas em que a pessoa é da equipe: as que o painel oferece na troca (CA-05, CA-06). */
export function empresasDaEquipe(me: Me): Me['empresas'] {
  return me.empresas.filter((e) => e.papel !== 'aluno');
}

export const NOME_DO_PAPEL: Record<Me['empresas'][number]['papel'], string> = {
  owner: 'Dono',
  admin: 'Administrador',
  profissional: 'Profissional',
  aluno: 'Aluno',
};

/** 2FA obrigatório: owner, admin e super-admin (RN-010). Os outros podem desativar. */
export function doisFatoresObrigatorio(me: Me): boolean {
  return me.superAdmin || me.papel === 'owner' || me.papel === 'admin';
}

export type Destino = { tipo: 'so-aluno' } | { tipo: 'ir'; para: string };

/** Para onde vai quem acabou de entrar (CA-01, CA-05, CA-09). */
export function destinoDepoisDoLogin(me: Me, volta: string | undefined): Destino {
  if (ehSoAluno(me)) return { tipo: 'so-aluno' };
  if (me.pendencias.includes('ATIVAR_2FA')) return { tipo: 'ir', para: '/ativar-2fa' };
  return { tipo: 'ir', para: voltaSegura(volta) ?? '/' };
}

export const MENSAGEM_SO_ALUNO =
  'O painel é para a equipe da academia. Use o app para ver seus treinos.';

/** Mensagens do login (CA-02 a CA-04, CA-08). */
export function mensagemDoLogin(erro: unknown): string {
  if (!eErroDaApi(erro)) return mensagemPara(erro);
  if (erro.status === 429) return 'Muitas tentativas. Tente de novo em alguns minutos.';
  switch (erro.authCode) {
    case 'INVALID_EMAIL_OR_PASSWORD':
      return 'Email ou senha incorretos.';
    case 'ACCESS_BLOCKED':
    case 'BANNED_USER':
      return 'Seu acesso está bloqueado. Fale com a academia.';
    case 'INVALID_CODE':
    case 'INVALID_BACKUP_CODE':
    case 'INVALID_TOTP':
      return 'Código inválido.';
    case 'INVALID_TWO_FACTOR_COOKIE':
      return 'A verificação expirou. Entre de novo.';
    default:
      return mensagemPara(erro);
  }
}

/** Erros de senha do Better Auth vão para o campo certo (CA-17). */
const CAMPO_DO_ERRO_DE_SENHA: Record<string, 'atual' | 'nova'> = {
  INVALID_PASSWORD: 'atual',
  PASSWORD_TOO_SHORT: 'nova',
  PASSWORD_TOO_LONG: 'nova',
  PASSWORD_TOO_COMMON: 'nova',
  PASSWORD_COMPROMISED: 'nova',
};

/**
 * Põe um erro de senha do Better Auth no campo (`atual` ou `nova`, pelos nomes dados).
 * Devolve `false` se o erro não é de senha.
 */
export function aplicarErroDeSenha<V extends FieldValues>(
  form: UseFormReturn<V>,
  erro: unknown,
  campos: { atual?: Path<V>; nova: Path<V> },
): boolean {
  if (!eErroDaApi(erro) || erro.authCode === undefined) return false;
  const qual = CAMPO_DO_ERRO_DE_SENHA[erro.authCode];
  const campo = qual === undefined ? undefined : campos[qual];
  if (campo === undefined) return false;
  form.setError(campo, { type: 'servidor', message: mensagemPara(erro) }, { shouldFocus: true });
  return true;
}

export function ehSessaoNaoRecente(erro: unknown): boolean {
  return eErroDaApi(erro) && erro.authCode === 'SESSION_NOT_FRESH';
}

export function ehLinkInvalido(erro: unknown): boolean {
  return (
    eErroDaApi(erro) && (erro.authCode === 'INVALID_TOKEN' || erro.authCode === 'TOKEN_EXPIRED')
  );
}

/** Regras da senha mostradas na tela (backend: RN de senha, mínimo 8 e sem senha comum). */
export const TAMANHO_MINIMO_DA_SENHA = 8;
export const TAMANHO_MAXIMO_DA_SENHA = 128;
